/**
 * ThemeContext — light / dark toggle with persistence.
 *
 * The class strategy is Tailwind's `darkMode: "class"` setting so we just
 * need to toggle `.dark` on the <html> element and every existing `dark:`
 * variant in the codebase (and all the CSS custom properties in index.css)
 * responds automatically.
 *
 * Persistence key: `pmp:theme` in localStorage.  When absent we honour
 * the OS `prefers-color-scheme` on first paint so we don't force a
 * different theme than the user's environment.
 *
 * A tiny inline `<script>` in public/index.html sets the class BEFORE
 * React hydrates so there is no FOUC on cold loads.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";

const STORAGE_KEY = "pmp:theme";

const ThemeContext = createContext({
  theme: "dark",
  setTheme: () => {},
  toggleTheme: () => {},
});

function readInitialTheme() {
  // Same rules as the inline hydration script — keep in sync.
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === "dark" || saved === "light") return saved;
  } catch (err) { if (typeof console !== 'undefined') console.debug('ThemeContext: swallowed', err); }
  try {
    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: light)").matches) {
      return "light";
    }
  } catch (err) { if (typeof console !== 'undefined') console.debug('ThemeContext: swallowed', err); }
  return "dark";  // Admin/vendor consoles have long defaulted to dark.
}

function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === "dark") {
    root.classList.add("dark");
    root.classList.remove("light");
  } else {
    root.classList.remove("dark");
    root.classList.add("light");
  }
  root.setAttribute("data-theme", theme);
  // Also drives the browser chrome — status bar / scrollbar colours.
  root.style.colorScheme = theme;
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() =>
    typeof window === "undefined" ? "dark" : readInitialTheme());

  // Apply on mount + on every change.  Persist to localStorage.
  useEffect(() => {
    applyTheme(theme);
    try { window.localStorage.setItem(STORAGE_KEY, theme); }
    catch (err) { if (typeof console !== 'undefined') console.debug('ThemeContext: swallowed', err); }
  }, [theme]);

  // Respond to OS-level changes only when the user hasn't picked an
  // explicit override.  We treat any localStorage value as an override.
  useEffect(() => {
    let mql;
    try { mql = window.matchMedia("(prefers-color-scheme: light)"); }
    catch (err) { if (typeof console !== 'undefined') console.debug('ThemeContext: swallowed', err); return; }
    const handler = (e) => {
      try {
        const override = window.localStorage.getItem(STORAGE_KEY);
        if (override === "dark" || override === "light") return;
      } catch (err) { if (typeof console !== 'undefined') console.debug('ThemeContext: swallowed', err); }
      setThemeState(e.matches ? "light" : "dark");
    };
    mql.addEventListener?.("change", handler);
    return () => mql.removeEventListener?.("change", handler);
  }, []);

  const setTheme = useCallback((next) => {
    if (next === "dark" || next === "light") setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((t) => (t === "dark" ? "light" : "dark"));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
